import { User } from 'lucide-react';
import type { BillingStatus } from '@/types';

const PLAN_LABEL_INLINE: Record<string, string> = {
  agency_starter: 'Agency Starter',
  agency_medium: 'Agency Medium',
  agency_professional: 'Agency Professional',
  sme_starter: 'Starter',
  sme_growth: 'Growth',
  sme_professional: 'Professional',
  freemium_trial: 'Trial',
};

function fmtDate(ms: number, language: string): string {
  return new Date(ms).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export interface ScheduledPlanChangeBannerProps {
  scheduled?: BillingStatus['scheduled'];
  cancelAtPeriodEnd?: BillingStatus['cancelAtPeriodEnd'];
  currentPlanLabel: string;
  owner?: BillingStatus['owner'];
  language: string;
}

/**
 * Surfaces pending Stripe/Autumn tier changes (downgrade at period end or
 * cancel-at-period-end). Same copy on Settings → Company/Plan and Profile → User/Plan.
 */
export function ScheduledPlanChangeBanner({
  scheduled,
  cancelAtPeriodEnd,
  currentPlanLabel,
  owner,
  language,
}: ScheduledPlanChangeBannerProps) {
  if (!scheduled && !cancelAtPeriodEnd) return null;

  return (
    <>
      {scheduled && (
        <div className="rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 flex items-start gap-3">
          <div className="rounded-md bg-warning/10 p-1.5 mt-0.5">
            <User className="h-4 w-4 text-warning" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {language === 'it'
                ? `Cambio piano programmato: ${PLAN_LABEL_INLINE[scheduled.planId] ?? scheduled.planId} dal ${fmtDate(scheduled.startsAt, language)}`
                : `Plan change scheduled: ${PLAN_LABEL_INLINE[scheduled.planId] ?? scheduled.planId} starting ${fmtDate(scheduled.startsAt, language)}`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {language === 'it'
                ? `Decisione del proprietario${owner?.name ? ' ' + owner.name : ''}. Il piano attuale (${currentPlanLabel}) resta attivo fino al ${fmtDate(scheduled.startsAt, language)}. Per modificare, accedi al profilo del proprietario.`
                : `Owner${owner?.name ? ' ' + owner.name : ''} scheduled this change. Current plan (${currentPlanLabel}) stays active until ${fmtDate(scheduled.startsAt, language)}. To modify, the owner must visit their profile.`}
            </p>
          </div>
        </div>
      )}
      {cancelAtPeriodEnd && (
        <div className="rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 flex items-start gap-3">
          <div className="rounded-md bg-warning/10 p-1.5 mt-0.5">
            <User className="h-4 w-4 text-warning" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {language === 'it'
                ? `Cancellazione programmata per il ${fmtDate(cancelAtPeriodEnd.periodEnd, language)}`
                : `Cancellation scheduled for ${fmtDate(cancelAtPeriodEnd.periodEnd, language)}`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {language === 'it'
                ? `Decisione del proprietario${owner?.name ? ' ' + owner.name : ''}. Dopo questa data l'azienda tornerà al piano di prova gratuito.`
                : `Owner${owner?.name ? ' ' + owner.name : ''} scheduled this cancellation. After this date the company reverts to the free trial.`}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
